import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Paper,
  Stack,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
  Chip,
  Divider,
} from '@mui/material';
import {
  AutoFixHigh,
  Security,
  Verified,
  ArrowForward,
  CloudUpload,
  AccountTree,
  CheckCircleOutline,
  Hub,
  Layers,
  Storage,
  MedicalServices,
  Cloud,
  SwapHoriz,
  Psychology,
  TableChart,
  Schedule,
  GppGood,
  FactCheck,
  Fingerprint,
  AutoMode,
  Warehouse,
  SyncAlt,
} from '@mui/icons-material';

// ─── Animated gradient keyframes via sx ───
const gradientAnimation = {
  '@keyframes gradientShift': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0px)' },
    '50%': { transform: 'translateY(-12px)' },
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.4 },
    '50%': { opacity: 0.8 },
  },
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  '@keyframes typeIn': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
};

// ─── Animated agent chat preview ───
interface ChatMessage {
  role: 'user' | 'agent';
  text: string;
  result?: string;
  review?: string; // review badge shown at crucial pipeline stages
}

const WorkspaceChatPreview: React.FC = () => {
  const messages: ChatMessage[] = [
    { role: 'user', text: 'Show me the top hospitals by patient volume' },
    {
      role: 'agent',
      text: 'Found 847K records across 3 sources. Here are the top 10 hospitals ranked by volume:',
      result: '10 results \u00b7 0.12s \u00b7 Added "top_hospitals" to your pipeline',
    },
    { role: 'user', text: 'Clean the hospital names \u2014 standardize formatting' },
    {
      role: 'agent',
      text: 'Standardized 847,291 hospital names \u2192 trimmed whitespace, normalized casing',
      result: 'Quality review passed \u00b7 0 issues found',
      review: 'Auto-Review: Transform Validated',
    },
    { role: 'user', text: 'Map this to our clinical data schema and export' },
    {
      role: 'agent',
      text: 'Mapped 42 fields to ClinicalData v2.1 \u00b7 98.5% confidence \u00b7 0 hallucinations',
      result: 'Exported to destination \u2714',
      review: 'Auto-Review: Mapping Verified \u00b7 Export Complete',
    },
  ];

  const [visibleCount, setVisibleCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount(0);
          let i = 0;
          const interval = setInterval(() => {
            i += 1;
            setVisibleCount(i);
            if (i >= messages.length) clearInterval(interval);
          }, 800);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha('#6366f1', 0.25)}`,
        bgcolor: '#0d0f1a',
        boxShadow: `0 24px 48px ${alpha('#000', 0.4)}, inset 0 1px 0 ${alpha('#fff', 0.05)}`,
      }}
    >
      {/* Title bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 2,
          py: 1.25,
          bgcolor: alpha('#fff', 0.04),
          borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
        }}
      >
        {['#ef4444', '#f59e0b', '#22c55e'].map((c) => (
          <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c, opacity: 0.8 }} />
        ))}
        <Typography
          sx={{
            ml: 1.5,
            fontSize: '0.72rem',
            fontFamily: 'monospace',
            color: alpha('#fff', 0.35),
            letterSpacing: '0.05em',
          }}
        >
          Migratr Workspace
        </Typography>
      </Box>

      {/* Chat body */}
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              opacity: visibleCount > i ? 1 : 0,
              transform: visibleCount > i ? 'translateY(0)' : 'translateY(6px)',
              transition: 'opacity 0.5s ease, transform 0.5s ease',
            }}
          >
            {/* Role indicator + message */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: msg.role === 'user' ? alpha('#3b82f6', 0.15) : alpha('#8b5cf6', 0.15),
                  color: msg.role === 'user' ? '#60a5fa' : '#a78bfa',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                {msg.role === 'user' ? 'U' : 'AI'}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    color: msg.role === 'user' ? alpha('#fff', 0.9) : alpha('#fff', 0.75),
                    fontSize: '0.88rem',
                    lineHeight: 1.6,
                    fontWeight: msg.role === 'user' ? 500 : 400,
                  }}
                >
                  {msg.text}
                </Typography>

                {/* Result line */}
                {msg.result && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                    <CheckCircleOutline sx={{ fontSize: 14, color: '#10b981' }} />
                    <Typography
                      sx={{
                        color: '#10b981',
                        fontSize: '0.78rem',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                      }}
                    >
                      {msg.result}
                    </Typography>
                  </Box>
                )}

                {/* Review badge at crucial stages */}
                {msg.review && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mt: 1.25,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1.5,
                      bgcolor: alpha('#8b5cf6', 0.12),
                      border: `1px solid ${alpha('#8b5cf6', 0.25)}`,
                    }}
                  >
                    <Verified sx={{ fontSize: 13, color: '#a78bfa' }} />
                    <Typography
                      sx={{
                        color: '#c4b5fd',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                      }}
                    >
                      {msg.review}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ))}

        {/* Blinking cursor at the end */}
        {visibleCount >= messages.length && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 5.5 }}>
            <Box
              sx={{
                width: 8,
                height: 16,
                bgcolor: '#8b5cf6',
                borderRadius: 0.5,
                animation: 'blink 1s step-end infinite',
              }}
            />
            <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.78rem' }}>
              Ask anything...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // ─── Stats ───
  const stats = [
    {
      value: '32+',
      label: 'Data Sources',
      sub: 'Connect to any enterprise database, warehouse, or cloud storage',
      color: '#6366f1',
    },
    {
      value: '10x',
      label: 'Faster Delivery',
      sub: 'From weeks of manual mapping to hours with AI assistance',
      color: '#06b6d4',
    },
    {
      value: '0',
      label: 'Hallucinated Mappings',
      sub: 'Every AI decision is constrained and validated before execution',
      color: '#10b981',
    },
    {
      value: '100%',
      label: 'Audit Coverage',
      sub: 'Every transformation tracked, versioned, and reviewable',
      color: '#f59e0b',
    },
  ];

  // ─── How It Works ───
  const steps = [
    {
      number: '01',
      icon: <CloudUpload sx={{ fontSize: 32 }} />,
      title: 'Connect & Profile',
      description:
        'Bring in data from databases, warehouses, cloud storage, or file uploads. Automatic profiling reveals structure, quality issues, and sensitive data the moment your data lands.',
      color: '#06b6d4',
    },
    {
      number: '02',
      icon: <Psychology sx={{ fontSize: 32 }} />,
      title: 'Explore & Transform',
      description:
        'Talk to your data in plain English. Build transformations visually, write code directly, or let AI generate them — all from a conversational workspace that tracks every action.',
      color: '#8b5cf6',
    },
    {
      number: '03',
      icon: <Verified sx={{ fontSize: 32 }} />,
      title: 'Map & Validate',
      description:
        'AI maps fields to governed target schemas with zero hallucinations. Data quality gates catch issues before anything ships. Every mapping is validated and auditable.',
      color: '#6366f1',
    },
    {
      number: '04',
      icon: <Storage sx={{ fontSize: 32 }} />,
      title: 'Ship & Schedule',
      description:
        'Export to any destination with one click — or schedule recurring runs to keep data flowing automatically. Reuse proven recipes across projects to accelerate future pipelines.',
      color: '#10b981',
    },
  ];

  // ─── Capabilities ───
  const capabilities = [
    {
      icon: <AccountTree />,
      title: 'Visual Pipeline Builder',
      description:
        'See your entire data flow as an interactive graph. Add steps, inspect results, preview changes, and track history — all from one canvas.',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    },
    {
      icon: <AutoFixHigh />,
      title: 'No-Code Transformations',
      description:
        'Filter, sort, group, join, and reshape data without writing a single line of code. Power users can drop into SQL or Python anytime.',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
    },
    {
      icon: <Psychology />,
      title: 'AI That Understands Context',
      description:
        'Smart suggestions based on your actual data. The AI detects quality issues — missing values, inconsistencies, outliers — and recommends fixes.',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
    },
    {
      icon: <Verified />,
      title: 'Guaranteed-Accurate Mapping',
      description:
        'AI maps fields using constrained decision spaces, not open-ended generation. Hallucinated or incorrect mappings are structurally impossible, not just unlikely.',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    },
    {
      icon: <Schedule />,
      title: 'Automated Scheduling',
      description:
        'Set up recurring pipeline runs on any schedule. Your data flows automatically — no manual triggers, no missed windows, no babysitting.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
    },
    {
      icon: <GppGood />,
      title: 'Schema Governance',
      description:
        'Manage target schemas through a controlled lifecycle — draft, review, approve, and promote. Every schema change is tracked and versioned before it reaches production.',
      gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    },
    {
      icon: <FactCheck />,
      title: 'Data Quality Gates',
      description:
        'Define validation rules that run before data ships. Catch type mismatches, missing required fields, and business rule violations before they reach your destination.',
      gradient: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
    },
    {
      icon: <Fingerprint />,
      title: 'PII Detection & Security',
      description:
        'Sensitive columns are automatically flagged during profiling. Multi-tenant isolation, role-based access, and complete audit trails keep your data compliant and secure.',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
    },
    {
      icon: <AutoMode />,
      title: 'Reusable Recipes',
      description:
        'Turn proven pipelines into reusable recipes. Apply them across projects with compatible schemas — no need to rebuild transformations and mappings from scratch every time.',
      gradient: 'linear-gradient(135deg, #84cc16 0%, #22c55e 100%)',
    },
    {
      icon: <Hub />,
      title: 'Universal Connectivity',
      description:
        'Connect to 32+ enterprise data sources and destinations — databases, warehouses, cloud storage, and APIs. One platform to ingest from anywhere and export to everywhere.',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
    },
  ];

  // ─── Use Cases ───
  const useCases = [
    {
      icon: <MedicalServices sx={{ fontSize: 28 }} />,
      title: 'Clinical Trial Data Harmonization',
      description:
        'Consolidate fragmented, multi-source clinical and hospital site datasets into unified, compliant global schemas — automatically.',
      color: '#6366f1',
    },
    {
      icon: <Cloud sx={{ fontSize: 28 }} />,
      title: 'Legacy System Migrations',
      description:
        'Accelerate transitions to the cloud by using AI to map thousands of legacy tables into modern data architectures.',
      color: '#06b6d4',
    },
    {
      icon: <Warehouse sx={{ fontSize: 28 }} />,
      title: 'Data Warehouse Consolidation',
      description:
        'Unify data from multiple SaaS platforms, databases, and file sources into a single warehouse with governed, validated schemas.',
      color: '#8b5cf6',
    },
    {
      icon: <SwapHoriz sx={{ fontSize: 28 }} />,
      title: 'Resilient Vendor Feeds',
      description:
        'Ingest volatile B2B partner payloads without pipeline breakages when upstream schemas inevitably drift.',
      color: '#10b981',
    },
    {
      icon: <SyncAlt sx={{ fontSize: 28 }} />,
      title: 'Cross-System Data Sync',
      description:
        'Keep data consistent across CRMs, ERPs, and operational databases with scheduled, repeatable pipelines and quality gates.',
      color: '#f59e0b',
    },
    {
      icon: <Security sx={{ fontSize: 28 }} />,
      title: 'Compliance-Driven Migrations',
      description:
        'Meet regulatory requirements with automatic PII detection, full audit trails, and governed schema promotion workflows.',
      color: '#ef4444',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', ...gradientAnimation }}>

      {/* ─── Navbar ─── */}
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          backdropFilter: 'blur(20px)',
          bgcolor: alpha(theme.palette.background.paper, 0.85),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 } }}>
            <Box
              component="img"
              src="/images/migratr.ai L.png"
              alt="migratr"
              sx={{ height: 24, width: 'auto', mr: 2, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                color="inherit"
                onClick={() => navigate('/login')}
                sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                endIcon={<ArrowForward sx={{ fontSize: '18px !important' }} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ─── Hero Section ─── */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 16, md: 20 },
          pb: { xs: 10, md: 14 },
          overflow: 'hidden',
        }}
      >
        {/* Decorative background */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(ellipse 80% 60% at 50% -20%, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 60%),
              radial-gradient(ellipse 60% 40% at 80% 50%, ${alpha('#8b5cf6', 0.06)} 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 20% 50%, ${alpha('#06b6d4', 0.06)} 0%, transparent 50%)
            `,
            zIndex: 0,
          }}
        />
        {/* Grid pattern overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.3,
            backgroundImage: `
              linear-gradient(${alpha(theme.palette.divider, 0.3)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha(theme.palette.divider, 0.3)} 1px, transparent 1px)
            `,
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse 70% 50% at 50% 50%, black 30%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 50% at 50% 50%, black 30%, transparent 70%)',
            zIndex: 0,
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Chip
            label="AI-Powered Data Engineering Platform"
            variant="outlined"
            size="small"
            icon={<Layers sx={{ fontSize: '16px !important' }} />}
            sx={{
              mb: 4,
              px: 1,
              fontWeight: 600,
              fontSize: '0.8rem',
              borderColor: alpha(theme.palette.primary.main, 0.3),
              color: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              '& .MuiChip-icon': { color: theme.palette.primary.main },
            }}
          />

          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2.2rem', sm: '3rem', md: '3.75rem' },
              lineHeight: 1.15,
              background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.primary.main} 100%)`,
              backgroundSize: '200% 200%',
              animation: 'gradientShift 6s ease infinite',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.03em',
            }}
          >
            Explore, Transform & Ship Data{' '}
            <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />
            with AI That Never Hallucinates
          </Typography>

          <Typography
            variant="h5"
            sx={{
              mb: 5,
              lineHeight: 1.75,
              maxWidth: 720,
              mx: 'auto',
              color: 'text.secondary',
              fontWeight: 400,
              fontSize: { xs: '1.05rem', md: '1.2rem' },
            }}
          >
            A conversational AI workspace where you explore data with natural language, build transforms on an interactive canvas,
            and map to target schemas with contract-driven AI — zero hallucinations, from ingestion to export.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/login')}
              sx={{
                px: 5,
                py: 1.75,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 2,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Request a Proof of Concept
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{
                px: 4,
                py: 1.75,
                fontSize: '1.05rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                borderColor: alpha(theme.palette.text.primary, 0.2),
                color: 'text.primary',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              See How It Works
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ─── Stats Bar ─── */}
      <Box sx={{ py: 6, bgcolor: alpha(theme.palette.primary.main, 0.02), borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {stats.map((stat, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 2,
                    px: 1,
                    borderRadius: 3,
                    border: `1px solid ${alpha(stat.color, 0.15)}`,
                    bgcolor: alpha(stat.color, 0.03),
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: alpha(stat.color, 0.07),
                      borderColor: alpha(stat.color, 0.3),
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      color: stat.color,
                      mb: 0.25,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="text.primary" mb={0.5}>
                    {stat.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" lineHeight={1.4} display="block">
                    {stat.sub}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── How It Works ─── */}
      <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Enterprise Workflow"
              size="small"
              sx={{
                mb: 2,
                fontWeight: 600,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.dark,
              }}
            />
            <Typography variant="h3" fontWeight={800} letterSpacing="-0.02em" mb={2}>
              How It Works
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight={400} maxWidth={580} mx="auto">
              Four steps from raw source data to validated, production-ready pipelines.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    position: 'relative',
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 16px 32px ${alpha(step.color, 0.12)}`,
                      borderColor: alpha(step.color, 0.35),
                    },
                  }}
                >
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 20,
                      fontSize: '3rem',
                      fontWeight: 900,
                      color: alpha(step.color, 0.07),
                      lineHeight: 1,
                    }}
                  >
                    {step.number}
                  </Typography>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(step.color, 0.1),
                      color: step.color,
                      mb: 3,
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} mb={1.5}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── Capabilities Section ─── */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Built for the Enterprise"
              size="small"
              sx={{
                mb: 2,
                fontWeight: 600,
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.dark,
              }}
            />
            <Typography variant="h3" fontWeight={800} letterSpacing="-0.02em" mb={2}>
              Capabilities
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight={400} maxWidth={580} mx="auto">
              Everything you need to go from raw data to production pipelines — with AI you can trust.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {capabilities.map((cap, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    gap: 3,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.06)}`,
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                      '& .cap-icon-box': {
                        transform: 'scale(1.05)',
                      },
                    },
                  }}
                >
                  <Box
                    className="cap-icon-box"
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: cap.gradient,
                      color: '#fff',
                      flexShrink: 0,
                      transition: 'transform 0.3s ease',
                      '& .MuiSvgIcon-root': { fontSize: 26 },
                    }}
                  >
                    {cap.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} mb={1}>
                      {cap.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
                      {cap.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── Use Cases + Terminal Graphic ─── */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          {/* Use Cases header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Use Cases"
              size="small"
              sx={{
                mb: 2,
                fontWeight: 600,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.dark,
              }}
            />
            <Typography variant="h3" fontWeight={800} letterSpacing="-0.02em" mb={2}>
              One Platform,{' '}
              <Box component="span" sx={{ color: theme.palette.primary.main }}>
                Endless Pipelines
              </Box>
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight={400} maxWidth={560} mx="auto">
              Whether you're harmonizing clinical data, migrating legacy systems, or consolidating warehouses — Migratr handles the complexity.
            </Typography>
          </Box>

          {/* Use case cards */}
          <Grid container spacing={3} mb={8}>
            {useCases.map((uc, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 16px 32px ${alpha(uc.color, 0.1)}`,
                      borderColor: alpha(uc.color, 0.3),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(uc.color, 0.1),
                      color: uc.color,
                      mb: 3,
                    }}
                  >
                    {uc.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} mb={1.5}>
                    {uc.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
                    {uc.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Terminal execution graphic */}
          <Box sx={{ maxWidth: 760, mx: 'auto' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={3} justifyContent="center">
              <Psychology sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" letterSpacing="0.05em" textTransform="uppercase" fontSize="0.8rem">
                Workspace
              </Typography>
            </Stack>
            <WorkspaceChatPreview />
          </Box>
        </Container>
      </Box>

      {/* ─── Final CTA Section ─── */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 5, md: 8 },
              borderRadius: 5,
              position: 'relative',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha('#4f46e5', 1)} 50%, ${theme.palette.primary.main} 100%)`,
              backgroundSize: '200% 200%',
              animation: 'gradientShift 8s ease infinite',
              boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.25)}`,
            }}
          >
            {/* Dot pattern */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 5,
                opacity: 0.1,
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            />
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: '#ffffff',
                  mb: 2,
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  letterSpacing: '-0.02em',
                }}
              >
                Ready to transform your data pipeline?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: alpha('#ffffff', 0.85),
                  mb: 5,
                  fontWeight: 400,
                  maxWidth: 500,
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.15rem' },
                }}
              >
                Join teams shipping AI-powered, zero-hallucination pipelines with Migratr.
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/login')}
                sx={{
                  px: 5,
                  py: 1.75,
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: 'none',
                  bgcolor: '#ffffff',
                  color: theme.palette.primary.dark,
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.9),
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                  boxShadow: `0 4px 12px ${alpha('#000000', 0.15)}`,
                }}
              >
                Request an Enterprise Demo
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ─── Footer ─── */}
      <Box
        component="footer"
        sx={{
          py: 6,
          bgcolor: '#fafbfc',
          borderTop: `1px solid ${theme.palette.divider}`,
          mt: 'auto',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src="/images/migratr.ai L.png"
                alt="migratr"
                sx={{ height: 42, width: 'auto', mb: 1.5, opacity: 0.85 }}
              />
              <Typography variant="body2" color="text.secondary" maxWidth={320}>
                AI-powered data engineering platform. Explore, transform, and ship data with zero hallucinations.
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={1.5}>
                Product
              </Typography>
              <Stack spacing={1}>
                {['Workspace', 'Capabilities', 'How It Works', 'Use Cases'].map((link) => (
                  <Typography
                    key={link}
                    variant="body2"
                    color="text.secondary"
                    sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' }, transition: 'color 0.15s' }}
                  >
                    {link}
                  </Typography>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={1.5}>
                Company
              </Typography>
              <Stack spacing={1}>
                {['Privacy Policy', 'Terms of Service', 'Contact'].map((link) => (
                  <Typography
                    key={link}
                    variant="body2"
                    color="text.secondary"
                    sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' }, transition: 'color 0.15s' }}
                  >
                    {link}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            &copy; {new Date().getFullYear()} Aventior. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
